<svelte:options tag={null} />

<script lang="ts">
  import {
    select,
    scaleBand,
    scaleLinear,
    axisBottom,
    axisLeft,
    Selection,
    ScaleBand,
    ScaleLinear,
    NumberValue,
    axisRight,
  } from 'd3';
  import { onMount } from 'svelte';
  import { EventHandler } from '../../utilities/EventHandler';
  import type {
    GroupedBarData,
    GroupedBarKeyValue,
    BarMargin,
  } from './grouped-bar-chart.types';

  export let data = '';
  let subgroups: string[];
  $: parsedData = data
    ? (JSON.parse(data) as GroupedBarData[])
    : ([] as GroupedBarData[]);
  $: groups = parsedData.map((d: GroupedBarData) => d.group);
  $: subgroups = parsedData[0]
    ? Object.keys(parsedData[0]).filter((d: string) => d !== 'group')
    : [];

  export let chartheight = '300';
  $: height = parseInt(chartheight, 10);

  export let backgroundcolor = '#fff';
  export let leftscalemax = 50;
  export let rightscalemax: number;
  export let color = '#0B96CC';
  export let ticknumbers = 4;
  export let tickleftformat: (d: NumberValue, i: number) => string = (d) =>
    `${Number(d)}`;
  export let tickbottomformat: (d: NumberValue, i: number) => string = (d) =>
    `${d}`;
  export let tickrightformat: (d: NumberValue, i: number) => string = (d) =>
    `${Number(d)}`;

  export let svgs: string;
  $: parsedSvgs = svgs ? (JSON.parse(svgs) as SVGElement[]) : [];

  export let margins: string;

  let _data: string;
  let chart: HTMLDivElement;
  let svg: Selection<SVGGElement, any, any, any>;
  let hasMounted = false;
  let tooltipData = { show: false, text: '', top: 0, left: 0, width: 0 };
  let eventHandler = new EventHandler();
  let leftScaleWidth = 0;
  let width = 0;

  $: if (data.length > 0 && hasMounted) {
    if (!_data) {
      render();
      _data = data;
    } else if (data !== _data) {
      update();
      _data = data;
    }
  }

  function getParsedMargins(margins: string): BarMargin {
    if (margins) return JSON.parse(margins) as BarMargin;
    else
      return {
        top: 10,
        right: 40,
        bottom: 20,
        left: 60,
        xAxis: 20,
      } as BarMargin;
  }

  function getChartWidth() {
    let chartWidth = select(chart).style('width');
    if (chartWidth === '' || !chartWidth) {
      chartWidth = `${window.innerWidth / 2}`;
    }
    return parseInt(chartWidth);
  }

  function getBarKeyValue(d: GroupedBarData): GroupedBarKeyValue[] {
    return subgroups.map((key) => ({
      key: key,
      value: d[key].value,
      scalePosition: d[key].scalePosition,
      color: d[key].color,
      customtooltip: d[key].customtooltip,
    }));
  }

  function getAxes(width: number) {
    // If there is no right scale, we only remove the right margin as the axis should continue to the edge of the chart
    const xWidth = rightscalemax
      ? width -
        (getParsedMargins(margins).left + getParsedMargins(margins).right)
      : width - getParsedMargins(margins).right;
    const x = scaleBand().domain(groups).range([0, xWidth]).padding(0.2);
    const yleft = scaleLinear().domain([0, leftscalemax]).range([height, 0]);
    const yright = scaleLinear().domain([0, rightscalemax]).range([height, 0]);
    const xSubgroup = scaleBand()
      .domain(subgroups)
      .range([0, x.bandwidth()])
      .padding(0.05);
    return { x, yleft, yright, xSubgroup };
  }

  function getAxisWidth(position: 'left' | 'right') {
    const { width } = chart
      .getElementsByClassName(`y-${position}-axis`)[0]
      .getBoundingClientRect();
    return width;
  }

  function addRectandles(
    x: ScaleBand<string>,
    xSubgroup: ScaleBand<string>,
    yleft: ScaleLinear<number, number, never>,
    yright: ScaleLinear<number, number, never>,
  ) {
    svg
      .append('g')
      .selectAll('g')
      .data(parsedData)
      .join('g')
      .attr('transform', (d) => `translate(${String(x(d.group))}, 0)`)
      .attr('class', 'bar')
      .selectAll('rect')
      .data((d) => getBarKeyValue(d))
      .join('rect')
      .attr('x', (d) => xSubgroup(d.key)!)
      .attr('y', (d) => (d.scalePosition === 'right' ? yright(0) : yleft(0)))
      .attr('width', xSubgroup.bandwidth())
      .attr(
        'height',
        (d) => height - (d.scalePosition === 'right' ? yright(0) : yleft(0)),
      )
      .style('fill', (d) => d.color || color)
      .style('cursor', 'pointer')
      .on('mouseout', (_e: MouseEvent, chartEvent: GroupedBarKeyValue) => {
        const others = svg.selectAll('rect').filter(function (arc) {
          return arc != chartEvent;
        });
        others.style('opacity', 1);
        tooltipData.show = false;
      })
      .on('mouseover', (e: MouseEvent, chartEvent: GroupedBarKeyValue) => {
        const others = svg.selectAll('rect').filter(function (arc) {
          return arc != chartEvent;
        });
        others.style('opacity', 0.7);
        const { left, top, width } = (
          e.target as HTMLElement
        ).parentElement?.getBoundingClientRect() ?? {
          left: 0,
          top: 0,
          width: 0,
        };
        tooltipData = {
          show: true,
          text: chartEvent.customtooltip
            ? `${chartEvent.customtooltip}`
            : `${chartEvent.key} - ${chartEvent.value}`,
          left: left,
          top: top - 50,
          width,
        };
      })

      .on('click', (_e: MouseEvent, chartEvent: GroupedBarKeyValue) => {
        eventHandler.dispatch('bar-clicked', chartEvent);
      });

    svg
      .selectAll<SVGElement, GroupedBarKeyValue>('rect')
      .transition()
      .duration(800)
      .attr('y', (d) =>
        d.scalePosition === 'right' ? yright(+d.value) : yleft(+d.value),
      )
      .attr(
        'height',
        (d) =>
          height -
          (d.scalePosition === 'right' ? yright(+d.value) : yleft(+d.value)),
      )
      .delay((_d, i) => i * 100);
  }

  function updateAxes(
    x: ScaleBand<string>,
    yleft: ScaleLinear<number, number, never>,
    yright: ScaleLinear<number, number, never>,
  ) {
    svg
      .select('.x-axis')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      .call((axisBottom(x) as any).tickFormat(tickbottomformat));
    svg
      .select('.y-left-axis')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      .call(
        (axisLeft(yleft) as any).ticks(ticknumbers).tickFormat(tickleftformat),
      );

    if (rightscalemax) {
      svg
        .select('.y-right-axis')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        .call(
          (axisRight(yright) as any)
            .ticks(ticknumbers)
            .tickFormat(tickrightformat),
        )
        .attr(
          'transform',
          // Right scale should be at width minus the width of the left scale plus its marging
          `translate(${
            width - getParsedMargins(margins).left - leftScaleWidth
          },0)`,
        );
    }

    // x2 is the end of left scale line and should stops where right scale starts (ie: same position)
    const x2 = rightscalemax
      ? width - getParsedMargins(margins).left - leftScaleWidth
      : width - leftScaleWidth;
    svg
      .selectAll('.y-left-axis .tick line')
      .attr('stroke', '#EEEDEA')
      .attr('stroke-width', '1px')
      .attr('x2', x2);
  }

  function render() {
    width = getChartWidth();
    const { x, yleft, yright, xSubgroup } = getAxes(width);

    svg = select<HTMLElement, any>(chart)
      .append('svg')
      .attr('width', width)
      .attr(
        'height',
        Number(height) +
          Number(getParsedMargins(margins).top) +
          Number(getParsedMargins(margins).bottom) +
          Number(getParsedMargins(margins).xAxis),
      )
      .attr('id', 'svg-chart')
      .append('g')
      .attr(
        'transform',
        `translate(${
          getParsedMargins(margins).left / 2 +
          getParsedMargins(margins).right / 2
        },${getParsedMargins(margins).top})`,
      );

    svg
      .append('g')
      .attr(
        'transform',
        `translate(0, ${
          Number(height) + Number(getParsedMargins(margins).xAxis)
        })`,
      )
      .call(axisBottom(x))
      .attr('class', 'x-axis')
      .attr('stroke-width', '0');

    svg
      .append('g')
      .call(axisLeft(yleft).ticks(ticknumbers).tickFormat(tickleftformat))
      .attr('class', 'y-left-axis')
      .attr('stroke-width', '0');
    leftScaleWidth = getAxisWidth('left');

    if (rightscalemax) {
      svg
        .append('g')
        .call(axisRight(yright).ticks(ticknumbers).tickFormat(tickleftformat))
        .attr('class', 'y-right-axis')
        .attr(
          'transform',
          // Right scale should be at width minus the width of the left scale plus its marging
          `translate(${
            width - getParsedMargins(margins).left - leftScaleWidth
          },0)`,
        )
        .attr('stroke-width', '0');
    }

    addRectandles(x, xSubgroup, yleft, yright);
  }

  function update() {
    chart.querySelector('#svg-chart')?.setAttribute('width', `${width}px`);

    const { x, yleft, yright, xSubgroup } = getAxes(width);
    updateAxes(x, yleft, yright);
    svg.selectAll('.bar').remove();
    addRectandles(x, xSubgroup, yleft, yright);
  }

  function resize() {
    width = getChartWidth();
    if (_data) {
      const width = getChartWidth();
      chart.querySelector('#svg-chart')?.setAttribute('width', `${width}px`);

      const { x, yleft, yright, xSubgroup } = getAxes(width);
      updateAxes(x, yleft, yright);

      svg
        .selectAll('.bar')
        .data(parsedData)
        .attr('transform', (d) => `translate(${String(x(d.group))}, 0)`)
        .selectAll<SVGElement, GroupedBarKeyValue>('rect')
        .data((d) => getBarKeyValue(d))
        .attr('x', (d) => xSubgroup(d.key)!)
        .attr('width', xSubgroup.bandwidth());
    }
  }

  function getLegendColor(
    subgroup: string,
    parsedData: GroupedBarData[],
  ): string {
    if (parsedData.length > 0) return parsedData[0][subgroup]?.color;
    return '';
  }

  onMount(() => {
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(chart);
    hasMounted = true;
  });
</script>

<div bind:this={chart} style="background-color: {backgroundcolor};" />

<div class="legend">
  {#each subgroups as subgroup}
    <div class="legend__item">
      <svg class="legend__box">
        <rect
          x="0"
          y="0"
          width="24"
          height="16"
          fill={getLegendColor(subgroup, parsedData)}
        />
      </svg>
      <span class="legend__title">{subgroup}</span>
    </div>
  {/each}
</div>

{#if tooltipData.show}
  <div
    class="tooltip"
    style={`top: ${tooltipData.top}px; left: ${tooltipData.left}px; width: ${tooltipData.width}px;`}
  >
    {tooltipData.text}
  </div>
{/if}

<div style="height: 0; width: 0;" class="pattern">
  {#each parsedSvgs as svg}
    {@html svg}
  {/each}
</div>
<text />

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';

  .legend {
    margin-top: magic-unit-rem(1);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 magic-unit-rem(1.5);
    height: 100%;
    color: $color-grey-999;
    gap: magic-unit-rem(1);

    &__item {
      display: flex;
      justify-content: center;
      font-size: $size-font-04;
      align-items: center;
    }

    &__title {
      white-space: nowrap;
      margin: 0;
    }

    &__box {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: magic-unit-rem(0.25);
      height: 16px;
      width: 24px;
    }
  }

  .tooltip {
    height: auto;
    background-color: $color-grey-700;
    border-radius: 4px;
    color: $color-grey-000;
    text-align: center;
    padding: magic-unit-rem(0.5) 0;
    position: absolute;
  }

  .pattern {
    z-index: -1;
    position: absolute;
  }

  text {
    font-family: 'LeroyMerlin', sans-serif;
    font-size: $size-font-04;
    color: $color-grey-700;
  }
</style>
